import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const PAGE_SIZE = 5;

const CountryDropdown = ({ idx, openDropdown, onSelect }) => {
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const listRef = useRef(null);
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    if (openDropdown !== idx) return;

    const token = localStorage.getItem("jwtToken");
    const headers = {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    setLoading(true);

    fetch(
      `${BASE_URL}/v1/user/countries?page=${page}&size=${PAGE_SIZE}&search=${search}`,
      { headers },
    )
      .then((res) => res.json())
      .then((res) => {
        const data = res?.data?.countries || [];

        setOptions((prev) => (page === 1 ? data : [...prev, ...data]));

        // If less than page size → no more data
        setHasMore(data.length === PAGE_SIZE);
      })
      .finally(() => setLoading(false));
  }, [search, page, openDropdown, idx]);

  // Infinite scroll
  const handleScroll = () => {
    if (!listRef.current || loading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;

    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setPage((prev) => prev + 1);
    }
  };

  // Handle search change
  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
    setOptions([]);
    setHasMore(true);
  };

  return (
    <AnimatePresence>
      {openDropdown === idx && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
          className="absolute left-0 mt-2 w-full bg-white border rounded-xl shadow-lg z-50"
        >
          {/* Search */}
          <input
            type="text"
            placeholder="Search country"
            value={search}
            onChange={handleSearch}
            className="w-full px-3 py-2 text-sm border-b focus:outline-none"
          />

          {/* Options */}
          <div
            ref={listRef}
            onScroll={handleScroll}
            className="overflow-y-auto"
            style={{
              maxHeight: `${PAGE_SIZE * 20}px`, // exactly 5 rows
            }}
          >
            {options.length > 0 ? (
              options.map((option) => (
                <motion.div
                  key={option.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => onSelect(option)}
                  className="px-4 py-2 text-sm cursor-pointer hover:bg-black hover:text-white"
                >
                  {option.name}
                </motion.div>
              ))
            ) : !loading ? (
              <div className="px-4 py-2 text-sm text-gray-500">
                No results found
              </div>
            ) : null}

            {loading && (
              <div className="px-4 py-2 text-xs text-gray-400">Loading…</div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CountryDropdown;
